import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { KeycloakService } from 'keycloak-angular';

import { AuthService } from '../../../core/services/utils/auth.service';

/**
 * Compte authentifié, mais sans fiche métier rattachée.
 *
 * <p>Le rôle Keycloak et la fiche en base sont deux actes distincts : un compte
 * peut exister sans que l'établissement ait créé la fiche correspondante. Le
 * jeton est alors valide mais amputé de son claim `id_professor` /
 * `id_student`, et toutes les requêtes répondent 401. Cet écran nomme la cause
 * et donne l'information dont l'administrateur aura besoin — plutôt que de
 * laisser l'utilisateur devant une suite d'erreurs sans explication.</p>
 */
@Component({
  selector: 'app-account-unlinked',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './account-unlinked.component.html',
  styleUrl: './account-unlinked.component.css',
})
export class AccountUnlinkedComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly auth = inject(AuthService);
  private readonly keycloak = inject(KeycloakService);

  readonly role = signal<'professor' | 'student'>('professor');
  readonly email = signal<string>('');
  readonly userId = signal<string>('');
  readonly copied = signal(false);

  ngOnInit(): void {
    const role = this.route.snapshot.queryParamMap.get('role');
    this.role.set(role === 'student' ? 'student' : 'professor');

    // L'administrateur a besoin de l'e-mail (pour créer la fiche) et de
    // l'identifiant technique (pour diagnostiquer un rattachement erroné).
    this.auth.getUser().then((user) => {
      this.email.set(user?.email ?? user?.username ?? '');
      this.userId.set(user?.id ?? '');
    });
  }

  get roleLabelKey(): string {
    return this.role() === 'student'
      ? 'ACCOUNT_UNLINKED.ROLE_STUDENT'
      : 'ACCOUNT_UNLINKED.ROLE_PROFESSOR';
  }

  /** Copie l'e-mail : c'est ce qu'on transmet à l'établissement. */
  async copyEmail(): Promise<void> {
    if (!this.email()) return;
    try {
      await navigator.clipboard.writeText(this.email());
      this.copied.set(true);
      setTimeout(() => this.copied.set(false), 2000);
    } catch {
      // Presse-papiers refusé (contexte non sécurisé, permission) : l'adresse
      // reste affichée et sélectionnable, rien n'est perdu.
    }
  }

  /** Réessaye : utile juste après que l'établissement a créé la fiche. */
  retry(): void {
    // Rechargement complet : le claim manquant est calculé à la validation du
    // jeton, il faut donc repartir d'une session neuve.
    window.location.href = '/';
  }

  logout(): void {
    this.keycloak.logout(window.location.origin);
  }
}
